import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import useIsMobile from 'hooks/useIsMobile';
import { useReceitas } from 'hooks/useReceitas';
import { useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, ListGroup, Row, Spinner } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { getPaciente } from 'services/pacientes';
import useSWR from 'swr';
import { podeExcluirReceita } from 'utils/gravacao';
import ModalProntuario from '../laudo/prontuario/ModalProntuario';
import { ehReceitaLivre, ROTULO_RECEITA_LIVRE } from './receitaLivre';
import SeletorMedicamentosPopover from './SeletorMedicamentosPopover';

export default function FormReceitas({
  codPac: codPacProp = null,
  consulta: consultaProp = null,
  embedded = false,
}) {
  const history = useHistory();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const codpac = codPacProp ?? query.get('codpac');

  const pacienteState = embedded ? null : location.state?.paciente;
  const consulta = consultaProp ?? location.state?.prontuario;
  const isMobile = useIsMobile();

  const [showModalProntuario, setShowModalProntuario] = useState(false);

  // Busca dados do paciente apenas quando não vêm via location.state
  const { data: pacienteApi, isLoading: isLoadingPaciente } = useSWR(
    codpac && !pacienteState ? ['paciente-dados', codpac] : null,
    ([, cod]) => getPaciente(cod)
  );

  const paciente = pacienteState || pacienteApi;

  const {
    receitas,
    error,
    loading: loadingReceitas,
    mutate,
    erroInfraestrutura,
    temReceitas,
    receitaSelecionada,
    isDefinitivo,
    isEditLocked,
    canMutate,
    descricao,
    tipoReceita,
    usoTipo,
    setTipoReceita,
    setUsoTipo,
    setCodReceitaSelecionada,
    atualizarDescricao,
    salvarDescricaoPendente,
    handleNovaReceita,
    handleRepetirReceita,
    handleSelecionarMedicamento,
    handleGravar,
    handleExcluir,
    handleImprimir,
  } = useReceitas({ codPac: codpac, consulta, ativo: true });

  const loading = loadingReceitas || isLoadingPaciente;
  const canEdit = !loading && receitaSelecionada && !isEditLocked;
  const podeExcluir =
    canMutate &&
    receitaSelecionada &&
    podeExcluirReceita(receitaSelecionada.GRAVACAO, receitaSelecionada.STATUS);
  const receitaLivre = ehReceitaLivre(tipoReceita);

  return (
    <Card className={embedded ? 'border-0' : 'shadow-sm border-0 my-3'}>
      <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          Receituário Médico - {paciente?.NOMEPAC || paciente?.NOME || 'Carregando...'}
        </h5>
        {loading && <Spinner animation="border" size="sm" variant="light" />}
      </Card.Header>

      <Card.Body>
        {error &&
          (erroInfraestrutura ? (
            <ServiceUnavailableAlert error={error} stale={temReceitas} onRetry={() => mutate()} />
          ) : (
            <Alert variant="danger">
              <p className="mb-0">
                {error?.response?.data?.message || 'Erro ao carregar receitas'}
              </p>
              <Button variant="outline-danger" size="sm" className="mt-3" onClick={() => mutate()}>
                Tentar novamente
              </Button>
            </Alert>
          ))}

        {(!error || temReceitas) && (
          <Row>
            {/* Coluna Esquerda: Histórico */}
            <Col md={3} lg={2} className="mb-3 mb-md-0">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="fw-bold text-muted">Histórico de Receitas</small>
                {receitas.length > 0 && <small className="text-muted">({receitas.length})</small>}
              </div>

              <ListGroup
                style={{
                  maxHeight: isMobile ? '215px' : '600px',
                  overflowY: 'auto',
                }}
              >
                {receitas.map((rec) => (
                  <ListGroup.Item
                    key={rec.CODRECEITA}
                    active={receitaSelecionada?.CODRECEITA === rec.CODRECEITA}
                    onClick={async () => {
                      await salvarDescricaoPendente();
                      setCodReceitaSelecionada(rec.CODRECEITA);
                    }}
                    style={{ cursor: 'pointer', padding: '8px 12px' }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{rec.dataFormatada}</span>
                      {rec.GRAVACAO === 'DEFINITIVO' && (
                        <span style={{ fontSize: '10px' }}>🔒</span>
                      )}
                    </div>
                    {ehReceitaLivre(rec) && (
                      <small className="d-block" style={{ fontSize: '11px' }}>
                        {ROTULO_RECEITA_LIVRE}
                      </small>
                    )}
                  </ListGroup.Item>
                ))}
                {receitas.length === 0 && !loading && (
                  <div className="text-center text-muted py-3">
                    <small>Nenhuma receita encontrada</small>
                  </div>
                )}
              </ListGroup>
            </Col>

            {/* Coluna Direita: Prescrição */}
            <Col md={9} lg={10}>
              <Row className="mb-2">
                <Col md={6}>
                  <Form.Label className="fw-bold">
                    Paciente: {paciente?.NOMEPAC || paciente?.NOME || ''}
                  </Form.Label>
                </Col>
                <Col md={6}>
                  <Form.Label className="fw-bold">
                    Médico: {receitaSelecionada?.NOMEMED || ''}
                  </Form.Label>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={4} className="mb-2 mb-md-0">
                  <Form.Label className="fw-bold">Tipo de Receita</Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      label="Normal"
                      checked={tipoReceita === 'Normal'}
                      onChange={() => setTipoReceita('Normal')}
                      disabled={isEditLocked}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Controlada"
                      checked={tipoReceita === 'Controlada'}
                      onChange={() => setTipoReceita('Controlada')}
                      disabled={isEditLocked}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Livre / Em branco"
                      checked={receitaLivre}
                      onChange={() => setTipoReceita('Livre')}
                      disabled={isEditLocked}
                    />
                  </div>
                </Col>

                {!receitaLivre && (
                  <>
                    <Col md={4} className="mb-2 mb-md-0">
                      <Form.Label className="fw-bold">Tipo de Uso</Form.Label>
                      <div>
                        <Form.Check
                          inline
                          type="radio"
                          label="Oral"
                          checked={usoTipo === 'oral'}
                          onChange={() => setUsoTipo('oral')}
                          disabled={isEditLocked}
                        />
                        <Form.Check
                          inline
                          type="radio"
                          label="Externo"
                          checked={usoTipo === 'externo'}
                          onChange={() => setUsoTipo('externo')}
                          disabled={isEditLocked}
                        />
                        <Form.Check
                          inline
                          type="radio"
                          label="Subcutâneo"
                          checked={usoTipo === 'subcutaneo'}
                          onChange={() => setUsoTipo('subcutaneo')}
                          disabled={isEditLocked}
                        />
                      </div>
                    </Col>

                    <Col md={4} className="d-flex align-items-end">
                      <SeletorMedicamentosPopover
                        disabled={isEditLocked}
                        onSelectMedicamento={handleSelecionarMedicamento}
                      />
                    </Col>
                  </>
                )}
              </Row>
              {receitaLivre && (
                <Alert variant="light" className="border py-2 small mb-3">
                  O PDF traz o logo da clínica, o título RECEITA, o nome do paciente, os dados do
                  médico, o texto digitado e o espaço de assinatura e carimbo. Sem data, QR Code ou
                  validação digital. Não é enviada pelo WhatsApp.
                </Alert>
              )}

              <Form.Group className="mb-3">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <Button
                    className="w-100 w-md-auto"
                    size="sm"
                    onClick={() => handleNovaReceita(paciente)}
                    disabled={loading || !canMutate}
                  >
                    Nova Receita
                  </Button>
                  <Button
                    className="w-100 w-md-auto"
                    size="sm"
                    variant="info"
                    onClick={() => handleRepetirReceita(paciente)}
                    disabled={loading || !receitaSelecionada || !canMutate}
                  >
                    Repetir Receita
                  </Button>

                  {consulta && (
                    <Button
                      className="w-100 w-md-auto"
                      size="sm"
                      variant="success"
                      onClick={() => setShowModalProntuario(true)}
                      disabled={loading}
                    >
                      Anamnese
                    </Button>
                  )}

                  {receitaSelecionada && (
                    <div className="ms-auto d-flex align-items-center">
                      <small className="text-muted me-2">
                        Receita #{receitaSelecionada.CODRECEITA} -{' '}
                        {receitaSelecionada.dataFormatada}
                      </small>
                      <Badge bg={isDefinitivo ? 'success' : 'warning'}>
                        {isDefinitivo ? 'DEFINITIVO' : 'PARCIAL'}
                      </Badge>
                    </div>
                  )}
                </div>

                <Form.Control
                  as="textarea"
                  rows={isMobile ? 10 : 14}
                  value={descricao}
                  onChange={(e) => atualizarDescricao(e.target.value)}
                  onBlur={salvarDescricaoPendente}
                  style={{ fontSize: '14px' }}
                  disabled={isEditLocked}
                />
              </Form.Group>

              <div className="d-flex flex-wrap justify-content-center gap-2">
                <Button
                  className="w-100 w-md-auto"
                  variant="outline-success"
                  onClick={() => handleGravar('PARCIAL')}
                  disabled={!canEdit}
                >
                  Gravar Parcial
                </Button>
                <Button
                  className="w-100 w-md-auto"
                  variant="outline-success"
                  onClick={() => handleGravar('DEFINITIVO')}
                  disabled={!canEdit}
                >
                  Gravar Definitivo
                </Button>
                {podeExcluir && (
                  <Button
                    className="w-100 w-md-auto"
                    variant="outline-danger"
                    onClick={handleExcluir}
                    disabled={loading}
                  >
                    Excluir
                  </Button>
                )}
                <Button
                  className="w-100 w-md-auto"
                  variant="warning"
                  onClick={() => handleImprimir()}
                  disabled={!receitaSelecionada || !isDefinitivo}
                >
                  Timbrado
                </Button>
                <Button
                  className="w-100 w-md-auto"
                  variant="outline-success"
                  onClick={() => handleImprimir()}
                  disabled={!receitaSelecionada || !isDefinitivo || receitaLivre}
                  title={
                    receitaLivre
                      ? 'Receita livre não possui validação digital e não é enviada pelo WhatsApp.'
                      : undefined
                  }
                >
                  <i className="bi bi-whatsapp me-1" style={{ color: '#25d366' }} />
                  Visualizar e Enviar
                </Button>
                {!embedded && (
                  <Button
                    className="w-100 w-md-auto"
                    variant="danger"
                    onClick={() => history.goBack()}
                    disabled={loading}
                  >
                    Voltar
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        )}
      </Card.Body>

      {showModalProntuario && (
        <ModalProntuario
          show={showModalProntuario}
          onHide={() => setShowModalProntuario(false)}
          prontuario={consulta}
        />
      )}
    </Card>
  );
}
